import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Pencere from './Pencere';

/**
 * SOR — tarayıcının `window.prompt` kutusunun yerine geçen tek alanlı giriş penceresi.
 *
 *   const neden = await sor({ baslik: 'Sipariş iptali', etiket: 'İptal nedeni', zorunlu: true, tehlike: true, onay: 'İptal et' });
 *   if (neden == null) return;   // vazgeçti
 *
 * `prompt` gibi söz (Promise) döner: metin = onaylandı, `null` = vazgeçildi. Pencere uygulamanın kökündeki
 * `<SorKapisi />` içinde çizilir; çağıran bileşene hiçbir şey eklemek gerekmez. Kısa veri girişleri için
 * kural: `window.prompt` KULLANILMAZ, bu kullanılır (birden çok alan gerekiyorsa `Pencere` ile özel pencere).
 */
export type Soru = {
  baslik: string;
  /** Başlığın altındaki ince satır (hangi kayıt: "S-0012 · Örnek Yapı"). */
  alt?: ReactNode;
  /** Alanın üstünde duran açıklama (ne olacağı). */
  aciklama?: ReactNode;
  etiket: string;
  ilk?: string; yerTutucu?: string;
  /** Alanın altındaki ipucu. */
  not?: string;
  tur?: 'metin' | 'cokSatir' | 'sayi' | 'adres';
  /** Boş bırakılamaz. */
  zorunlu?: boolean;
  azami?: number;
  /** Geçersizse hata metni, geçerliyse null. Boş değer için çağrılmaz (onu `zorunlu` belirler). */
  denetle?: (deger: string) => string | null;
  /** Onay düğmesinin yazısı (varsayılan "Kaydet"). */
  onay?: string;
  /** Geri alınması zor işlem: onay düğmesi kırmızı olur. */
  tehlike?: boolean;
};

type Acik = { soru: Soru; bitir: (v: string | null) => void; no: number };
let acik: Acik | null = null;
let sayac = 0;
const dinleyenler = new Set<() => void>();
const duyur = () => dinleyenler.forEach((d) => d());

export function sor(soru: Soru): Promise<string | null> {
  return new Promise((coz) => {
    acik?.bitir(null);   // üst üste iki soru açılmaz; öncekinden vazgeçilmiş sayılır
    acik = { soru, no: ++sayac, bitir: (v) => { acik = null; duyur(); coz(v); } };
    duyur();
  });
}

function SoruPenceresi({ a }: { a: Acik }) {
  const s = a.soru;
  const [deger, setDeger] = useState(s.ilk ?? '');
  const [dokundu, setDokundu] = useState(false);
  const temiz = deger.trim();
  const kusur = !temiz ? (s.zorunlu ? `${s.etiket} yazın.` : null) : s.denetle?.(temiz) ?? null;
  const gonder = () => { setDokundu(true); if (!kusur) a.bitir(temiz); };
  /** Yazılmış ama geçersiz değer anında, boş zorunlu alan ise kaydetmeye çalışınca uyarılır. */
  const goster = Boolean(kusur) && (Boolean(temiz) || dokundu);
  const ortak = {
    id: 'ccd-sor-alan', className: s.tur === 'cokSatir' ? 'ccd-textarea' : 'ccd-input', value: deger, autoFocus: true, placeholder: s.yerTutucu,
    maxLength: s.azami ?? (s.tur === 'cokSatir' ? 2000 : 500), 'aria-invalid': goster, 'aria-describedby': 'ccd-sor-not',
    onChange: (e: { target: { value: string } }) => setDeger(e.target.value),
  };
  return (
    <Pencere baslik={s.baslik} alt={s.alt} kapat={() => a.bitir(null)} gonder={gonder}
      eylem={<>
        <button type="button" className="ccd-btn ccd-btn-ghost" onClick={() => a.bitir(null)}>Vazgeç</button>
        <button type="submit" className={`ccd-btn ${s.tehlike ? 'ccd-btn-danger' : 'ccd-btn-primary'}`} disabled={Boolean(kusur)}>{s.onay ?? 'Kaydet'}</button>
      </>}>
      {s.aciklama && <div className="ccd-sor-aciklama">{s.aciklama}</div>}
      <div className="ccd-field ccd-giris" data-durum={goster ? 'hatali' : undefined}>
        <label className="ccd-label" htmlFor="ccd-sor-alan">{s.etiket}{!s.zorunlu && <span className="ccd-meta"> · isteğe bağlı</span>}</label>
        {s.tur === 'cokSatir'
          ? <textarea {...ortak} rows={3} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); gonder(); } }} />
          : <input {...ortak} type={s.tur === 'adres' ? 'url' : 'text'} inputMode={s.tur === 'sayi' ? 'decimal' : s.tur === 'adres' ? 'url' : 'text'}
                   autoComplete="off" autoCapitalize={s.tur === 'adres' ? 'none' : undefined} spellCheck={s.tur === 'adres' || s.tur === 'sayi' ? false : undefined}
                   onFocus={(e) => { if (s.ilk) e.target.select(); }} />}
        <div id="ccd-sor-not" className={goster ? 'ccd-field-error' : 'ccd-field-hint'} aria-live="polite">{goster ? kusur : s.not ?? ''}</div>
      </div>
    </Pencere>
  );
}

/** Uygulamanın kökünde BİR kez durur; açık soruyu çizer. Sayfa değişirse açık sorudan vazgeçilmiş sayılır. */
export function SorKapisi() {
  const a = useSyncExternalStore((d) => { dinleyenler.add(d); return () => dinleyenler.delete(d); }, () => acik);
  useEffect(() => {
    const geri = () => acik?.bitir(null);
    window.addEventListener('popstate', geri);
    return () => window.removeEventListener('popstate', geri);
  }, []);
  if (!a) return null;
  return createPortal(<div className="ccd" style={{ display: 'contents' }}><SoruPenceresi key={a.no} a={a} /></div>, document.body);
}

/** Sık kullanılan denetimler. */
export const adresDenetle = (v: string) => (/^https?:\/\/\S+\.\S+$/i.test(v) ? null : 'Adres http:// ya da https:// ile başlamalı (örnek: https://site.com/sayfa).');
export const sayiOku = (v: string) => Number(v.trim().replace(/\./g, '').replace(',', '.'));
